import { useMemo } from "react";
import { formatVehicleNumberDisplay } from "../../../utils/vehicleNumberUtils";
import { getInventoryStatus } from "./confirmationListUtils";
import {
  actionLabel,
  buildMobileExcelRows,
  formatReportedAt,
  isRowClickable,
  rowClassName,
  showInventoryColumn,
  statusBadgeClass,
} from "./confirmationExcelListHelpers";
import {
  assemblePendingVehicleGroups,
  formatVehicleRequestCount,
} from "./groupPendingConfirmations";

function InventoryBadge({ item }) {
  const inv = getInventoryStatus(item);
  if (inv.key === "na") return "—";
  return (
    <span className={inv.badgeClass} title={inv.label}>
      {inv.label}
    </span>
  );
}

function ConfirmationMobileExcelCard({
  item,
  serial,
  isRepoAdmin,
  showInventoryCol,
  onActivate,
  forceViewAction = false,
}) {
  const clickable = forceViewAction || isRowClickable(item, isRepoAdmin);
  const label = forceViewAction ? "View" : actionLabel(item, isRepoAdmin);
  const rows = [
    { label: "S.No.", value: String(serial) },
    ...buildMobileExcelRows(item, { isRepoAdmin, showInventoryCol }),
  ];

  return (
    <article
      className={`cf-excel-list-mobile-item ${rowClassName(item, isRepoAdmin)}`}
      onClick={() => {
        if (clickable) onActivate(item);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && clickable) onActivate(item);
      }}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <div className="cf-excel-mobile-stack">
        {rows.map((row, index) => (
          <div key={`${row.label}-${index}`} className="cf-excel-mobile-row">
            <span className="cf-excel-mobile-row__label">{row.label}</span>
            <span className="cf-excel-mobile-row__value">
              {row.isStatus ? (
                <span className={statusBadgeClass(item.status)}>{row.value}</span>
              ) : row.inventory ? (
                <span className={row.inventory.badgeClass}>{row.value}</span>
              ) : (
                row.value
              )}
            </span>
          </div>
        ))}
      </div>
      {clickable && label !== "—" ? (
        <div className="cf-excel-list-mobile-foot">
          <button
            type="button"
            className="cf-open-btn"
            onClick={(e) => {
              e.stopPropagation();
              onActivate(item);
            }}
          >
            {label}
          </button>
        </div>
      ) : null}
    </article>
  );
}

function ConfirmationDesktopRow({
  item,
  serial,
  isRepoAdmin,
  showTracerCols,
  showConfirmedByCol,
  showInventoryCol,
  nested = false,
  onActivate,
  forceViewAction = false,
}) {
  const clickable = forceViewAction || isRowClickable(item, isRepoAdmin);
  const label = forceViewAction ? "View" : actionLabel(item, isRepoAdmin);

  return (
    <tr
      className={`${rowClassName(item, isRepoAdmin)}${nested ? " cf-excel-nested-row" : ""}`}
      onClick={() => {
        if (clickable) onActivate(item);
      }}
    >
      <td>{serial}</td>
      <td>{formatVehicleNumberDisplay(item.vehicleNumber) || "—"}</td>
      <td>{item.customerName || "—"}</td>
      {isRepoAdmin ? <td>{item.bankName || "—"}</td> : null}
      {isRepoAdmin ? <td>{item.branchName || "—"}</td> : null}
      <td>
        <span className={statusBadgeClass(item.status)}>{item.status || "—"}</span>
      </td>
      {showTracerCols ? <td>{item.requestedByName || "—"}</td> : null}
      {showTracerCols ? (
        <td>{item.requestedByRoleLabel || item.requestedByRole || "—"}</td>
      ) : null}
      {showTracerCols ? <td>{item.requestedByPhone || "—"}</td> : null}
      {showConfirmedByCol ? <td>{item.reviewedByName || "—"}</td> : null}
      <td className="cf-excel-note">{item.requestNote || "—"}</td>
      <td>{formatReportedAt(item.createdAt)}</td>
      {showInventoryCol ? (
        <td className="cf-inv-td">
          <InventoryBadge item={item} />
        </td>
      ) : null}
      <td className="cf-action-td" onClick={(e) => e.stopPropagation()}>
        {clickable && label !== "—" ? (
          <button
            type="button"
            className={`cf-open-btn${
              label === "Upload Inventory" || label === "Update Inventory"
                ? " cf-open-btn--inv"
                : ""
            }`}
            onClick={() => onActivate(item)}
          >
            {label}
          </button>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}

export default function ConfirmationExcelList({
  items,
  groups = null,
  groupByVehicle = false,
  isRepoAdmin,
  listFilter,
  startSerial = 1,
  onView,
  onInventory,
  onOpenGroup,
  forceViewAction = false,
}) {
  const showInventoryCol = showInventoryColumn(listFilter);
  const showTracerCols = isRepoAdmin;
  const showConfirmedByCol = showInventoryColumn(listFilter);

  const vehicleGroups = useMemo(
    () => (groupByVehicle ? assemblePendingVehicleGroups(items, groups) : []),
    [groupByVehicle, items, groups]
  );

  const handleActivate = (item) => {
    if (forceViewAction) {
      onView(item);
      return;
    }
    const status = String(item?.status || "").toUpperCase();
    if (isRepoAdmin) {
      if (status === "CONFIRMED") {
        const inv = getInventoryStatus(item);
        if (inv.key === "pending-upload" || inv.key === "revision") {
          onInventory(item);
          return;
        }
      }
      onView(item);
      return;
    }
    if (status === "CONFIRMED") {
      onInventory(item);
    }
  };

  const tableHead = (
    <tr>
      <th>S.No.</th>
      <th>Registration Number</th>
      <th>Customer Name</th>
      {isRepoAdmin ? <th>Bank Name</th> : null}
      {isRepoAdmin ? <th>Branch Name</th> : null}
      <th>Status</th>
      {showTracerCols ? <th>Traced By</th> : null}
      {showTracerCols ? <th>Role</th> : null}
      {showTracerCols ? <th>Reporter Mobile</th> : null}
      {showConfirmedByCol ? <th>Confirmed By</th> : null}
      <th>Field Note</th>
      <th>Reported At</th>
      {showInventoryCol ? <th className="cf-inv-th">Inventory</th> : null}
      <th className="cf-action-th">Action</th>
    </tr>
  );

  return (
    <div className="cf-excel-list-responsive">
      <div className="cf-excel-list-mobile" aria-label="Confirmations list">
        {groupByVehicle
          ? vehicleGroups.map((group, index) => {
              const latest = group.requests[0];
              const serial = startSerial - index;
              return (
                <article
                  key={group.vehicleKey}
                  className="cf-excel-list-mobile-item confirmation-row-pending cf-excel-group-card confirmation-row-clickable"
                  onClick={() => onOpenGroup?.(group)}
                >
                  <div className="cf-excel-group-toggle">
                    <span className="cf-excel-group-toggle__body">
                      <strong>
                        {formatVehicleNumberDisplay(group.vehicleNumber || latest?.vehicleNumber) ||
                          "—"}
                      </strong>
                      <span className="cf-excel-request-count">
                        {formatVehicleRequestCount(group.requestCount)}
                      </span>
                      <span className="cf-excel-group-toggle__meta">
                        {latest?.customerName || "—"}
                        {isRepoAdmin && latest?.bankName ? ` · ${latest.bankName}` : ""}
                        {` · S.No. ${serial}`}
                      </span>
                    </span>
                  </div>
                  <div className="cf-excel-list-mobile-foot">
                    <button
                      type="button"
                      className="cf-open-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenGroup?.(group);
                      }}
                    >
                      View requests
                    </button>
                  </div>
                </article>
              );
            })
          : items.map((item, index) => (
              <ConfirmationMobileExcelCard
                key={item._id}
                item={item}
                serial={startSerial - index}
                isRepoAdmin={isRepoAdmin}
                showInventoryCol={showInventoryCol}
                onActivate={handleActivate}
                forceViewAction={forceViewAction}
              />
            ))}
      </div>

      <div className="cf-excel-list-desktop company-table-wrap cf-excel-sheet cf-excel-sheet--list">
        <p className="cf-excel-scroll-hint" aria-hidden="true">
          Scroll horizontally to see all columns
        </p>
        <div className="table-scroll">
          <table className="users-table excel-grid-table cf-excel-list-table">
            <thead>{tableHead}</thead>
            <tbody>
              {groupByVehicle
                ? vehicleGroups.map((group, index) => {
                    const latest = group.requests[0] || {};
                    const serial = startSerial - index;
                    return (
                      <tr
                        key={group.vehicleKey}
                        className="confirmation-row-pending cf-excel-group-row confirmation-row-clickable"
                        onClick={() => onOpenGroup?.(group)}
                      >
                        <td>{serial}</td>
                        <td>
                          <span className="cf-excel-group-plate">
                            <span>
                              {formatVehicleNumberDisplay(
                                group.vehicleNumber || latest.vehicleNumber
                              ) || "—"}
                            </span>
                            <span className="cf-excel-request-count">
                              {formatVehicleRequestCount(group.requestCount)}
                            </span>
                          </span>
                        </td>
                        <td>{latest.customerName || "—"}</td>
                        {isRepoAdmin ? <td>{latest.bankName || "—"}</td> : null}
                        {isRepoAdmin ? <td>{latest.branchName || "—"}</td> : null}
                        <td>
                          <span className={statusBadgeClass(latest.status || "PENDING")}>
                            {latest.status || "PENDING"}
                          </span>
                        </td>
                        {showTracerCols ? (
                          <td>
                            {group.requestCount > 1
                              ? "Multiple"
                              : latest.requestedByName || "—"}
                          </td>
                        ) : null}
                        {showTracerCols ? (
                          <td>
                            {group.requestCount > 1
                              ? "—"
                              : latest.requestedByRoleLabel || latest.requestedByRole || "—"}
                          </td>
                        ) : null}
                        {showTracerCols ? (
                          <td>
                            {group.requestCount > 1 ? "—" : latest.requestedByPhone || "—"}
                          </td>
                        ) : null}
                        {showConfirmedByCol ? <td>—</td> : null}
                        <td className="cf-excel-note">—</td>
                        <td>{formatReportedAt(latest.createdAt)}</td>
                        {showInventoryCol ? <td className="cf-inv-td">—</td> : null}
                        <td className="cf-action-td" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="cf-open-btn"
                            onClick={() => onOpenGroup?.(group)}
                          >
                            View requests
                          </button>
                        </td>
                      </tr>
                    );
                  })
                : items.map((item, index) => (
                    <ConfirmationDesktopRow
                      key={item._id}
                      item={item}
                      serial={startSerial - index}
                      isRepoAdmin={isRepoAdmin}
                      showTracerCols={showTracerCols}
                      showConfirmedByCol={showConfirmedByCol}
                      showInventoryCol={showInventoryCol}
                      onActivate={handleActivate}
                      forceViewAction={forceViewAction}
                    />
                  ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
